import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { selectUserToken } from "../../../../../features/userSlice";
import { familiesServices } from "../../../../../services/api/families.endpoints";
import { familiesEndPointLogic } from "../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import ConnectabilityConfirmModal from "../../shared/ConnectabilityConfirmModal";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";
import { buildCandidatesFromThings, type CandidateItem } from "../../shared/candidateCatalogue";
import NeedAttentionAddFamiliesModalSkeleton from "./NeedAttentionAddFamiliesModalSkeleton";

function parseJsonField(value: any) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

interface Props {
  open: boolean;
  missingItem: string;
  stepTitle: string;
  handleClose: () => void;
  isSwitchMode?: boolean;
  // Real-submit context — omitted where the caller hasn't wired a family yet.
  familyId?: string | number;
  familyName?: string; // sent back unchanged — the real PUT always expects ui_family_name
  stepKey?: "fetcher" | "unifier" | "advisor";
  backendInterfaceId?: string | number;
  brandsCount?: number;
  existingMapIds?: (string | number)[];
  switchingId?: string | number; // the one id being replaced, when isSwitchMode
  onSubmitted?: () => void;
  // Staged mode (used by EditFamilyModal's batched Save): instead of PUTing
  // immediately, hand the computed full id set back to the caller and let
  // it accumulate multiple staged changes before its own Save/connectability
  // check. When provided, this takes priority over the immediate-PUT path.
  onStageChange?: (mapIds: (string | number)[]) => void;
}

function Candidate({
  item,
  staged,
  onToggle,
}: {
  item: CandidateItem;
  staged: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      onClick={staged ? undefined : onToggle}
      sx={{
        border: `1px solid ${staged ? "#9C27B0" : "#e0e0e0"}`,
        backgroundColor: staged ? "#F8F2FB" : "#fff",
        borderRadius: "8px",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        mb: 1,
        cursor: staged ? "default" : "pointer",
        "&:hover": staged ? {} : { borderColor: "#9C27B0" },
      }}
    >
      <Box>
        <Typography sx={{ color: "#9C27B0", fontSize: "14px", fontWeight: 500 }}>
          {item.name}{" "}
          <Box component="span" sx={{ color: "#00000061", fontSize: "12px", fontWeight: 500 }}>
            ({item.id})
          </Box>
        </Typography>
        <Typography sx={{ color: "#00000099", fontSize: "12px", letterSpacing: "0.4px" }}>
          {item.description}
        </Typography>
      </Box>

      {staged && (
        <IconButton
          size="small"
          onClick={onToggle}
          sx={{
            width: 22,
            height: 22,
            backgroundColor: "#9C27B0",
            color: "#fff",
            "&:hover": { backgroundColor: "#7B1FA2" },
          }}
        >
          <CloseIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      )}
    </Box>
  );
}

const NeedAttentionAddFamiliesModal = ({
  open,
  missingItem,
  stepTitle,
  handleClose,
  isSwitchMode = false,
  familyId,
  familyName,
  stepKey,
  backendInterfaceId,
  brandsCount = 0,
  existingMapIds = [],
  switchingId,
  onSubmitted,
  onStageChange,
}: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const website_shared_uid = useWebsiteSharedUid();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [candidates, setCandidates] = useState<CandidateItem[]>([]);
  const [isCatalogueLoading, setIsCatalogueLoading] = useState(false);
  // Q1 now returns each family's own shared_uid (family_shared_uid) — the
  // real URL id for PUT, replacing the earlier website_shared_uid
  // placeholder. Resolved from the same Q1 fetch already made for candidates.
  const [familySharedUid, setFamilySharedUid] = useState<string | number | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    mapIds: (string | number)[];
    message?: string;
  } | null>(null);

  // This modal has no Q1 data of its own (unlike EditFamilyModal/FamilyDrawer,
  // which already have it loaded) — fetch it here so the candidate list is
  // real instead of a fixed placeholder.
  useEffect(() => {
    if (!open || !backendInterfaceId || !stepKey) return;
    let cancelled = false;
    setIsCatalogueLoading(true);
    // Never show a stale list from a previous open while the fresh one is
    // loading — the loading state below fully replaces this content anyway,
    // but clearing it too avoids any flash of old data if that ever changes.
    setCandidates([]);
    setFamilySharedUid(null);
    familiesServices
      .getFamiliesOverview(oauthTokenHeader, website_shared_uid, backendInterfaceId)
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.data?.query_output ?? [];
        const thingsForStep = rows
          .filter((row: any) => row.step === stepKey)
          .flatMap((row: any) => parseJsonField(row.things));
        setCandidates(buildCandidatesFromThings(thingsForStep));

        const match = rows.find(
          (row: any) => row.family_id === familyId && row.step === stepKey,
        );
        if (match) setFamilySharedUid(match.family_shared_uid ?? null);
      })
      .finally(() => {
        if (!cancelled) setIsCatalogueLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, backendInterfaceId, stepKey, familyId, oauthTokenHeader, website_shared_uid]);

  const singular = missingItem.replace(/s$/i, "").toLowerCase();

  const toggle = (id: string | number) => {
    setSelectedIds((prev) => {
      if (prev.some((x) => String(x) === String(id))) {
        return prev.filter((x) => String(x) !== String(id));
      }
      // switching replaces one-for-one — selecting a new candidate
      // swaps out whatever was previously selected
      return isSwitchMode ? [id] : [...prev, id];
    });
  };

  const staged = useMemo(
    () => candidates.filter((item) => selectedIds.some((id) => String(id) === String(item.id))),
    [candidates, selectedIds],
  );
  // Not-yet-staged candidates, independent of the search box — this backs
  // the always-visible list, which should only ever shrink as items get
  // staged, never react to what's typed into the search field.
  const notStaged = useMemo(
    () => candidates.filter((item) => !selectedIds.some((id) => String(id) === String(item.id))),
    [candidates, selectedIds],
  );
  // The Autocomplete dropdown's own options — same base set, filtered by search.
  const remaining = useMemo(
    () => notStaged.filter((item) => item.name.toLowerCase().includes(search.toLowerCase())),
    [notStaged, search],
  );

  const handleCloseAndReset = () => {
    setSearch("");
    setSelectedIds([]);
    handleClose();
  };

  const buildNewMapIds = (): (string | number)[] => {
    if (isSwitchMode) {
      const withoutOld = switchingId
        ? existingMapIds.filter((id) => String(id) !== String(switchingId))
        : existingMapIds;
      return [...withoutOld, ...selectedIds];
    }
    return [...existingMapIds, ...selectedIds];
  };

  const submitMapIds = async (mapIds: (string | number)[]) => {
    if (!familyId || !familySharedUid) return;
    setIsSubmitting(true);
    const success = await familiesEndPointLogic.updateFamily(
      oauthTokenHeader,
      familySharedUid,
      { step: stepKey, backendInterfaceId },
      { ui_family_name: familyName, map_ids: mapIds },
      dispatch,
    );
    setIsSubmitting(false);
    if (success) {
      handleCloseAndReset();
      onSubmitted?.();
    }
  };

  const handleSubmit = async () => {
    const mapIds = buildNewMapIds();

    if (onStageChange) {
      onStageChange(mapIds);
      handleCloseAndReset();
      return;
    }

    if (!familyId) {
      // Not wired to a real family context — nothing to submit against.
      handleCloseAndReset();
      return;
    }

    // Only a removal (switch, which drops the old id) can break something —
    // adding never does, so only gate the switch path, and only when this
    // family actually has brands connected.
    if (isSwitchMode && brandsCount > 0) {
      setIsSubmitting(true);
      const { risky, message } = await familiesEndPointLogic.checkFamiliesConnectability(
        oauthTokenHeader,
        { family_id: familyId, backend_interface_id: backendInterfaceId, map_ids: mapIds },
      );
      setIsSubmitting(false);
      if (risky) {
        setPendingConfirm({ mapIds, message });
        return;
      }
    }

    await submitMapIds(mapIds);
  };

  const handleConfirmRisky = async () => {
    if (!pendingConfirm) return;
    const { mapIds } = pendingConfirm;
    setPendingConfirm(null);
    await submitMapIds(mapIds);
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{ sx: { width: "550px", maxWidth: "550px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <DialogTitle sx={{ padding: "0px 0px 10px 0px" }}>
              {isSwitchMode ? `Switch ${singular}` : `Add ${missingItem}`}
            </DialogTitle>
            <IconButton size="small" onClick={handleCloseAndReset}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {isCatalogueLoading ? (
            <NeedAttentionAddFamiliesModalSkeleton />
          ) : (
            <>
              <Autocomplete
                size="small"
                options={remaining}
                inputValue={search}
                onInputChange={(_, val) => setSearch(val)}
                onChange={(_, val) => {
                  if (val) toggle(val.id);
                }}
                getOptionLabel={(item) => item.name}
                filterOptions={(opts) => opts}
                noOptionsText={`No available ${missingItem.toLowerCase()}.`}
                renderOption={(props, item) => (
                  <Box component="li" {...props} key={item.id}>
                    <Box>
                      <Typography sx={{ color: "#9C27B0", fontSize: 14, fontWeight: 500 }}>
                        {item.name}{" "}
                        <Box component="span" sx={{ color: "#00000061", fontSize: 12, fontWeight: 500 }}>
                          ({item.id})
                        </Box>
                      </Typography>
                      <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Search ${missingItem}`}
                    color="secondary"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ fontSize: 20, color: "#666", mr: 0.5 }} />,
                    }}
                  />
                )}
              />

              <Typography sx={{ fontSize: 12, color: "#00000099", mt: 1 }}>
                Adding a {singular} moves it into this family for the {stepTitle} step.
              </Typography>

              {staged.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {staged.map((item) => (
                    <Candidate key={item.id} item={item} staged onToggle={() => toggle(item.id)} />
                  ))}
                </Box>
              )}

              <Box
                sx={{
                  mt: 2,
                  maxHeight: "220px",
                  overflow: "auto",
                  border: "1px solid #F0F0F3",
                  borderRadius: "8px",
                  p: 1,
                }}
              >
                {notStaged.length > 0 ? (
                  notStaged.map((item) => (
                    <Candidate key={item.id} item={item} staged={false} onToggle={() => toggle(item.id)} />
                  ))
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#00000099", textAlign: "center", p: 2 }}>
                    No available {missingItem.toLowerCase()}.
                  </Typography>
                )}
              </Box>
            </>
          )}

          <Stack flexDirection={"row"} justifyContent={"space-between"} marginTop={2}>
            <Button variant="outlined" color="secondary" onClick={handleCloseAndReset}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={isSubmitting || selectedIds.length === 0}
              startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {isSwitchMode
                ? `Switch ${singular}`
                : `Add ${
                    selectedIds.length > 1
                      ? `${selectedIds.length} ${missingItem.toLowerCase()}`
                      : singular
                  }`}
            </Button>
          </Stack>
        </Paper>
      </Dialog>

      <ConnectabilityConfirmModal
        open={!!pendingConfirm}
        message={pendingConfirm?.message}
        isSubmitting={isSubmitting}
        handleCancel={() => setPendingConfirm(null)}
        handleConfirm={handleConfirmRisky}
      />
    </>
  );
};

export default NeedAttentionAddFamiliesModal;
